import type { JSONContent } from "@tiptap/react";
import { type useNavigate, Link } from "react-router-dom";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { TemplatePicker } from "@/components/ui/TemplatePicker";
import { type useTocNavigation } from "@/components/editor/useTocNavigation";
import { type useFloatInsert } from "@/components/editor/useFloatInsert";
import { type useEditorHandlers } from "@/components/editor/useEditorHandlers";
import { type useEditorState } from "@/components/editor/useEditorState";
import { EditorHeader } from "@/components/editor/EditorHeader";
import { EditorBody } from "@/components/editor/EditorBody";
import { useEditorPage } from "@/components/editor/useEditorPage";
import { isSeedId } from "@/store/seedTemplates";
import type { Template } from "@/types/template";
import type { Evaluation } from "@/types/evaluation";

interface EditorPageProps {
  mode: "build" | "fill";
}

function getStatusLabel(saveStatus: string): string {
  switch (saveStatus) {
    case "saving": {
      return "Saving\u2026";
    }
    case "saved": {
      return "All changes saved";
    }
    case "error": {
      return "Save failed";
    }
    default: {
      return "All changes saved";
    }
  }
}

function EditorGuard({
  evaluation,
  mode,
  template,
  unifiedDocument,
}: Readonly<{
  mode: "build" | "fill";
  template: Template | undefined;
  evaluation: Evaluation | undefined;
  unifiedDocument: unknown;
}>) {
  if (mode === "build" && !template) {
    return <div className="placeholder">Template not found</div>;
  }
  if (mode === "fill" && !evaluation) {
    return (
      <div className="placeholder">
        Evaluation not found. <Link to="/fill">Back to list</Link>
      </div>
    );
  }
  if (mode === "fill" && !unifiedDocument) {
    return <div className="placeholder">Loading&hellip;</div>;
  }
  return null;
}

function EditorPageHeader({
  handlers,
  mode,
  navigate,
  saveStatus,
  state,
  template,
}: Readonly<{
  mode: "build" | "fill";
  state: ReturnType<typeof useEditorState>;
  handlers: ReturnType<typeof useEditorHandlers>;
  template: Template | undefined;
  saveStatus: string;
  navigate: ReturnType<typeof useNavigate>;
}>) {
  const displayName = mode === "build" ? state.meta.name : state.name;
  return (
    <EditorHeader
      mode={mode}
      displayName={displayName}
      meta={state.meta}
      saveStatus={saveStatus}
      statusLabel={getStatusLabel(saveStatus)}
      storagePrefix={state.storagePrefix}
      showSource={state.showSource}
      setShowSource={state.setShowSource}
      showReport={state.showReport}
      setShowReport={state.setShowReport}
      templateId={template?.id}
      isSeed={isSeedId(template?.id ?? "")}
      onNameChange={handlers.handleNameChange}
      onCategoryChange={handlers.handleCategoryChange}
      onCopyReport={handlers.handleCopyReport}
      onMarkCompleted={handlers.handleMarkCompleted}
      onSaveAs={() => void handlers.handleSaveAs()}
      onDeleteClick={() => {
        state.setShowDeleteConfirm(true);
      }}
      menuOpen={state.menuOpen}
      setMenuOpen={state.setMenuOpen}
      onNavigateBack={() => navigate(mode === "build" ? "/templates" : "/fill")}
    />
  );
}

interface EditorPageBodyProps {
  mode: "build" | "fill";
  state: ReturnType<typeof useEditorState>;
  handlers: ReturnType<typeof useEditorHandlers>;
  toc: ReturnType<typeof useTocNavigation>;
  float: ReturnType<typeof useFloatInsert>;
  interpolate: (s: string) => string;
  reportSections: { sectionId?: string; lines: string[] }[];
  syncDocToMd: (doc: JSONContent) => void;
  evaluation: Evaluation | undefined;
}

function buildBodyStateProps(s: ReturnType<typeof useEditorState>) {
  return {
    codeMirrorContainerRef: s.codeMirrorContainerRef,
    collapsedSections: s.collapsedSections,
    editorRef: s.editorRef,
    fieldToggles: s.fieldToggles,
    parseErrors: s.parseErrors,
    reportPaneRef: s.reportPaneRef,
    sectionToggles: s.sectionToggles,
    setEditorWrapperEl: s.setEditorWrapperEl,
    setShowInsertPicker: s.setShowInsertPicker,
    setUnifiedDocument: s.setUnifiedDocument,
    setVariableValues: s.setVariableValues,
    showReport: s.showReport,
    showSource: s.showSource,
    subTemplateValues: s.subTemplateValues,
    tiptapEditingRef: s.tiptapEditingRef,
    tiptapEditingTimerRef: s.tiptapEditingTimerRef,
    unifiedDocument: s.unifiedDocument,
    values: s.values,
    variableValues: s.variableValues,
  };
}

function buildEditorBodyProps(p: EditorPageBodyProps) {
  const { float: f, handlers: h, toc: t } = p;
  return {
    interpolate: p.interpolate,
    mode: p.mode,
    reportSections: p.reportSections,
    syncDocToMd: p.syncDocToMd,
    ...buildBodyStateProps(p.state),
    activeHeadingIndex: t.activeHeadingIndex,
    allCollapsed: h.allCollapsed,
    floatBtn: f.floatBtn,
    floatBtnElRef: f.floatBtnElRef,
    floatMenuOpen: f.floatMenuOpen,
    handleCollapseAll: h.handleCollapseAll,
    handleCollapseSection: h.handleCollapseSection,
    handleExpandAll: h.handleExpandAll,
    handleTocNavigate: t.handleTocNavigate,
    handleToggleField: h.handleToggleField,
    handleToggleSection: h.handleToggleSection,
    hideTimerRef: f.hideTimerRef,
    pendingInsertPosRef: f.pendingInsertPosRef,
    sectionIds: h.sectionIds,
    setFieldValue: h.setFieldValue,
    setFloatBtn: f.setFloatBtn,
    setFloatMenuOpen: f.setFloatMenuOpen,
    setSubTemplateValue: h.setSubTemplateValue,
    setTocPopoverOpen: t.setTocPopoverOpen,
    setTocPopoverPos: t.setTocPopoverPos,
    tocBtnRef: t.tocBtnRef,
    tocEntries: t.tocEntries,
    tocPopoverOpen: t.tocPopoverOpen,
    tocPopoverPos: t.tocPopoverPos,
    variables:
      p.mode === "build" ? (p.state.meta.variables ?? []) : (p.evaluation?.variables ?? []),
  } as const;
}

function EditorPageBody(props: Readonly<EditorPageBodyProps>) {
  return <EditorBody {...buildEditorBodyProps(props)} />;
}

function EditorDialogs({
  handlers,
  mode,
  state,
}: Readonly<{
  mode: "build" | "fill";
  state: ReturnType<typeof useEditorState>;
  handlers: ReturnType<typeof useEditorHandlers>;
}>) {
  const displayName = mode === "build" ? state.meta.name : state.name;
  return (
    <>
      {mode === "fill" && state.showInsertPicker && (
        <TemplatePicker
          title="Insert Template"
          templates={handlers.pickerTemplates}
          onSelect={handlers.handleInsertTemplate}
          onClose={() => {
            state.setShowInsertPicker(false);
          }}
        />
      )}
      <ConfirmDialog
        open={state.showDeleteConfirm}
        title={mode === "build" ? "Delete Template" : "Delete Evaluation"}
        message={`Are you sure you want to delete "${displayName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => void handlers.handleDelete()}
        onCancel={() => {
          state.setShowDeleteConfirm(false);
        }}
      />
    </>
  );
}

export function EditorPage({ mode }: Readonly<EditorPageProps>) {
  const { features, handlers, navigate, saveStatus, state, stores } = useEditorPage(mode);

  const guard = EditorGuard({
    evaluation: stores.evaluation,
    mode,
    template: stores.template,
    unifiedDocument: state.unifiedDocument,
  });
  if (guard) {
    return guard;
  }

  return (
    <div className="unified-outer">
      <EditorPageHeader
        mode={mode}
        state={state}
        handlers={handlers}
        template={stores.template}
        saveStatus={saveStatus}
        navigate={navigate}
      />
      <EditorPageBody
        mode={mode}
        state={state}
        handlers={handlers}
        toc={features.toc}
        float={features.float}
        interpolate={features.interpolate}
        reportSections={features.reportSections}
        syncDocToMd={features.syncDocToMd}
        evaluation={stores.evaluation}
      />
      <EditorDialogs mode={mode} state={state} handlers={handlers} />
    </div>
  );
}
